import { ActionContext } from 'vuex';
import { QuantList } from '@/models/jobQuants';
import { State } from '@/models/main';

export interface ModuleQuantCartState {
  items: QuantList[];
}

const STORAGE_KEY = 'quant_cart';

export const moduleQuantCart = {
  namespaced: true,

  state: (): ModuleQuantCartState => ({
    items: [],
  }),

  mutations: {
    ADD_ITEM(state: ModuleQuantCartState, item: QuantList) {
      const existingIndex = state.items.findIndex(i => i.id === item.id);
      if (existingIndex === -1) {
        state.items.push(item);
      }
    },

    REMOVE_ITEM(state: ModuleQuantCartState, itemId: number) {
      state.items = state.items.filter(item => item.id !== itemId);
    },

    CLEAR_CART(state: ModuleQuantCartState) {
      state.items = [];
    },

    LOAD_FROM_STORAGE(state: ModuleQuantCartState, items: QuantList[]) {
      state.items = items || [];
    },

    UPDATE_CUSTOMER_CODE(state: ModuleQuantCartState, customerCode: string) {
      if (state.items.length > 0 && state.items[0].owner_id) {
        state.items[0].owner_id[1] = customerCode;
      }
    },
  },

  actions: {
    addItem({ commit, dispatch }: ActionContext<ModuleQuantCartState, State>, item: QuantList) {
      commit('ADD_ITEM', item);
      dispatch('saveToStorage');
    },

    removeItem({ commit, dispatch }: ActionContext<ModuleQuantCartState, State>, itemId: number) {
      commit('REMOVE_ITEM', itemId);
      dispatch('saveToStorage');
    },

    clearCart({ commit }: ActionContext<ModuleQuantCartState, State>) {
      commit('CLEAR_CART');
      localStorage.removeItem(STORAGE_KEY);
    },

    saveToStorage({ state }: ActionContext<ModuleQuantCartState, State>) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state.items));
    },

    loadFromStorage({ commit }: ActionContext<ModuleQuantCartState, State>) {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          const items = JSON.parse(stored);
          if (Array.isArray(items)) {
            commit('LOAD_FROM_STORAGE', items);
          } else {
            localStorage.removeItem(STORAGE_KEY);
          }
        }
      } catch (error) {
        localStorage.removeItem(STORAGE_KEY);
      }
    },

    updateCustomerCode({ commit, dispatch }: ActionContext<ModuleQuantCartState, State>, customerCode: string) {
      commit('UPDATE_CUSTOMER_CODE', customerCode);
      dispatch('saveToStorage');
    },
  },

  getters: {
    cartItems: (state: ModuleQuantCartState) => state.items,
    cartCount: (state: ModuleQuantCartState) => state.items.length,
    totalCartCount(state: ModuleQuantCartState) {
      return state.items.length + state.items.filter(item => item.link_quant_id).length;
    },
    linkedQuantsCount(state: ModuleQuantCartState) {
      return state.items.filter(item => item.link_quant_id).length;
    },
    cartItemIds: (state: ModuleQuantCartState) => state.items.map(item => item.id),
    hasLinkedQuants(state: ModuleQuantCartState) {
      return state.items.some(item => item.link_quant_id);
    },
    cartWarehouse: (state: ModuleQuantCartState) => {
      if (state.items.length === 0) {
        return '';
      }
      return state.items[0].warehouse_code || '';
    },
    isInCart: (state: ModuleQuantCartState) => (itemId: number) => {
      return state.items.some(item => item.id === itemId);
    },
  },
};
